const sentryConfig = window.__STOREFRONT_SENTRY__;
const TOKEN_PATH_SEGMENT_PATTERN =
  /^[0-9a-z]{8}(?:-[0-9a-z]{4}){3}-[0-9a-z]{12}$/i;

function getSentryEnvelopeUrl(dsn) {
  try {
    const parsedDsn = new URL(dsn);
    const projectId = parsedDsn.pathname.replace(/^\/+/, "").split("/").pop();
    if (!projectId || !parsedDsn.username) {
      return null;
    }
    const sentryKey = encodeURIComponent(parsedDsn.username);
    return `${parsedDsn.protocol}//${parsedDsn.host}/api/${projectId}/envelope/?sentry_version=7&sentry_key=${sentryKey}&sentry_client=storefront-js`;
  } catch (error) {
    return null;
  }
}

function randomHex(length) {
  const fallbackHex = () =>
    Array.from({ length }, () =>
      Math.floor(Math.random() * 16).toString(16)
    ).join("");

  if (!window.crypto || !window.crypto.getRandomValues) {
    return fallbackHex();
  }

  const bytes = new Uint8Array(Math.ceil(length / 2));
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, length);
}

function parseStackLine(line) {
  // Chrome / Edge / Node style
  let match = line.match(/^\s*at\s+(?:(.*?)\s+\()?(.+?):(\d+):(\d+)\)?$/);
  if (match) {
    return {
      function: match[1] || "<anonymous>",
      filename: match[2],
      lineno: Number(match[3]),
      colno: Number(match[4]),
    };
  }

  // Firefox / Safari style
  match = line.match(/^(.*?)@(.+?):(\d+):(\d+)$/);
  if (match) {
    return {
      function: match[1] || "<anonymous>",
      filename: match[2],
      lineno: Number(match[3]),
      colno: Number(match[4]),
    };
  }

  return null;
}

function buildStacktrace(stack) {
  if (!stack) {
    return null;
  }

  const lines = String(stack)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  // JS stacks are most-recent-first; Sentry expects oldest-first.
  const frames = lines
    .map(parseStackLine)
    .filter(Boolean)
    .map((frame) => ({
      ...frame,
      filename: sanitizeUrl(frame.filename, frame.filename),
    }))
    .reverse();

  if (!frames.length) {
    return null;
  }

  return {
    frames,
  };
}

function getCurrentOrigin() {
  return (
    window.location.origin ||
    `${window.location.protocol}//${window.location.host}`
  );
}

function sanitizeUrl(value, fallback) {
  if (!value) {
    return fallback || value;
  }

  try {
    const parsedUrl = value.startsWith("//")
      ? new URL(`${window.location.protocol}${value}`)
      : new URL(value);

    parsedUrl.search = "";
    parsedUrl.hash = "";
    parsedUrl.pathname = parsedUrl.pathname
      .split("/")
      .map((part) =>
        TOKEN_PATH_SEGMENT_PATTERN.test(part)
          ? `redacted-${part.slice(-8).toUpperCase()}`
          : part
      )
      .join("/");

    return parsedUrl.href;
  } catch (error) {
    return fallback || value;
  }
}

function sanitizeCurrentUrl() {
  return sanitizeUrl(window.location.href, getCurrentOrigin());
}

function eventFromError(errorInfo, defaults) {
  const error = errorInfo && errorInfo.error;
  const message = error
    ? error.message
    : errorInfo && errorInfo.message
    ? errorInfo.message
    : "Unknown storefront error";
  const type = error && error.name ? error.name : "Error";

  const exception = { type, value: message };
  const stacktrace = buildStacktrace(error && error.stack);
  if (stacktrace) {
    exception.stacktrace = stacktrace;
  }

  return {
    ...defaults,
    message,
    culprit:
      errorInfo && errorInfo.filename
        ? sanitizeUrl(errorInfo.filename, errorInfo.filename)
        : sanitizeCurrentUrl(),
    exception: {
      values: [exception],
    },
  };
}

function eventFromRejection(rejection, defaults) {
  const reason = rejection && rejection.reason;
  const message =
    reason && reason.message
      ? reason.message
      : String(reason || "Unhandled promise rejection");
  const type = reason && reason.name ? reason.name : "UnhandledRejection";

  const exception = { type, value: message };
  const stacktrace = buildStacktrace(reason && reason.stack);
  if (stacktrace) {
    exception.stacktrace = stacktrace;
  }

  return {
    ...defaults,
    message,
    exception: {
      values: [exception],
    },
  };
}

if (
  sentryConfig &&
  sentryConfig.dsn &&
  !window.__STOREFRONT_SENTRY_INITIALIZED__
) {
  const sentryEnvelopeUrl = getSentryEnvelopeUrl(sentryConfig.dsn);
  if (sentryEnvelopeUrl) {
    window.__STOREFRONT_SENTRY_INITIALIZED__ = true;

    const sendToSentry = (eventPayload) => {
      const envelopeHeaders = {
        event_id: eventPayload.event_id,
        sent_at: new Date().toISOString(),
        sdk: {
          name: "storefront-js",
          version: "1.0.0",
        },
      };
      const itemHeaders = { type: "event" };
      const envelope = `${JSON.stringify(envelopeHeaders)}\n${JSON.stringify(
        itemHeaders
      )}\n${JSON.stringify(eventPayload)}`;

      if (navigator.sendBeacon) {
        const didQueue = navigator.sendBeacon(
          sentryEnvelopeUrl,
          new Blob([envelope], { type: "text/plain;charset=UTF-8" })
        );
        if (didQueue) {
          return;
        }
      }

      fetch(sentryEnvelopeUrl, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        body: envelope,
        keepalive: true,
        mode: "cors",
      }).catch(() => {});
    };

    const baseEvent = () => ({
      event_id: randomHex(32),
      timestamp: new Date().toISOString(),
      level: "error",
      logger: "storefront.javascript",
      platform: "javascript",
      user: {
        ip_address: null,
      },
      tags: {
        app: "storefront",
      },
      request: {
        url: sanitizeCurrentUrl(),
      },
    });

    window.addEventListener("error", (errorInfo) => {
      try {
        sendToSentry(eventFromError(errorInfo, baseEvent()));
      } catch (error) {}
    });

    window.addEventListener("unhandledrejection", (rejection) => {
      try {
        sendToSentry(eventFromRejection(rejection, baseEvent()));
      } catch (error) {}
    });
  }
}
